import React, { useState } from 'react';
import { flatten } from 'ramda';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import Modal from 'components/ui/Modal';
import Checkbox from 'components/ui/Checkbox';
import CurrencySymbol from 'components/ui/CurrencySymbol';
import SectionHeaderBar from 'components/ui/SectionHeaderBar';
import { productsShape, userShape } from 'constants/propTypes';
import { WRITE_OFF_REASONS } from 'constants/reasons';

import { groupHash } from '../Payments.utils';
import Product from '../Summary/Product';

import {
  Text,
  BookingFee,
  CheckboxWrapper,
  BookingFeeBody,
  BookingFeeFooter,
  ReasonLabel,
  ReasonSection,
  ReasonSelect,
  CancelRefund,
  ConfirmRefund,
  CalculateRefund,
  ExplanationLabel,
  AmountToBeRefunded,
  ExplanationTextArea
} from './BookingFees.styles';
import { THALYS_PASS_CAN_FEE } from 'types/fees';

const BookingFees = ({
  toBeRefunded,
  intl,
  isRefunding,
  isCancelling,
  isConfirming,
  onRefund,
  onCancel,
  onConfirm,
  user,
  bookingStatus,
  bookingFees,
  disabled,
  disabledTooltipMessageId
}) => {
  const { formatMessage } = intl;
  const [showModal, setModal] = useState(false);
  const [checkedItems, setItems] = useState({});
  const [reasonCode, setReasonCode] = useState('');
  const [label, setLabel] = useState('');
  const [additionalExplanation, setAdditionalExplanation] = useState('');

  const loading = [isRefunding, isCancelling, isConfirming].some(Boolean);

  const reason = reasonCode && WRITE_OFF_REASONS.find(({ code }) => code === reasonCode);

  const hasOptions = flatten(Object.values(checkedItems)).length > 0;
  const showAdditionalExplanation = Boolean(reason && reason.requiresAdditional);
  const disabledTooltipMessage = disabledTooltipMessageId && formatMessage ? formatMessage({ id: disabledTooltipMessageId }) : '';

  const canSubmitExplanation = showAdditionalExplanation ? !additionalExplanation.match(/^\s*$/) : true;
  const canCalculateRefund = Boolean(!loading && reasonCode && canSubmitExplanation);

  const handleSubmit = async (event) => {
    event.preventDefault();

    await onRefund({
      itemRefs: flatten(Object.values(checkedItems)),
      reasonCode,
      additionalExplanation,
      label
    });

    setModal(true);
    setItems({});
    setReasonCode('');
    setLabel('');
    setAdditionalExplanation('');
  };

  const handleConfirm = async () => {
    await onConfirm();
    setModal(false);
  };

  const handleCancel = async () => {
    await onCancel();
    setModal(false);
  };

  const updateItem = (bookingFee, quantity) => {
    setItems({
      ...checkedItems,
      [groupHash(bookingFee)]: quantity ? bookingFee.itemRefs.slice(0, quantity) : []
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <SectionHeaderBar>{formatMessage({ id: 'paymentsTab.bookingFees' })}</SectionHeaderBar>
      <BookingFeeBody>
        {bookingFees.length > 0
          ? bookingFees.map((bookingFee) => {
              const isCancelled = bookingFee.cancelled;
              const itemHash = groupHash(bookingFee);
              const hasItems = Boolean(checkedItems[itemHash] && checkedItems[itemHash].length);
              const checkboxTooltipId = `booking-fee-disabled-tooltip-${itemHash}`;

              return (
                !isCancelled && (
                  <BookingFee key={itemHash} data-cy="booking-fee">
                    <CheckboxWrapper
                      data-for={disabledTooltipMessage ? checkboxTooltipId : undefined}
                      data-tip={disabledTooltipMessage || undefined}
                    >
                      <Checkbox
                        dataCy="booking-fee-checkbox"
                        checked={hasItems}
                        disabled={user.isReadOnly || bookingFee.code === THALYS_PASS_CAN_FEE || disabled}
                        onChange={(checked) => {
                          if (checked) {
                            updateItem(bookingFee, bookingFee.itemRefs.length);
                          } else {
                            updateItem(bookingFee, 0);
                          }
                        }}
                      />
                      {disabledTooltipMessage && <ReactTooltip id={checkboxTooltipId} data-cy="booking-fee-disabled-tooltip" />}
                    </CheckboxWrapper>

                    <Product
                      key={itemHash}
                      product={bookingFee}
                      onChange={(quantity) => updateItem(bookingFee, quantity)}
                      editable={hasItems}
                    />
                  </BookingFee>
                )
              );
            })
          : formatMessage({ id: 'paymentsTab.none' })}
      </BookingFeeBody>

      {hasOptions && (
        <BookingFeeFooter>
          <ReasonSection>
            <ReasonLabel>
              <Text>{formatMessage({ id: 'paymentsTab.reason' })}</Text>

              <ReasonSelect
                value={`${reasonCode}_${label}`}
                required
                defaultValue=""
                data-cy="booking-fees-reason"
                onChange={(event) => {
                  const [code, label] = event.target.value.split('_');
                  setReasonCode(code);
                  setLabel(label);
                  setAdditionalExplanation('');
                }}
              >
                <option value="" disabled>
                  {formatMessage({ id: 'paymentsTab.defaultReason' })}
                </option>
                {WRITE_OFF_REASONS.map(({ code, label }) => (
                  <option key={code} value={`${code}_${label}`}>
                    {label}
                  </option>
                ))}
              </ReasonSelect>
            </ReasonLabel>

            {showAdditionalExplanation && (
              <ExplanationLabel>
                <Text>{formatMessage({ id: 'paymentsTab.additionalExplanation' })}</Text>

                <ExplanationTextArea
                  value={additionalExplanation}
                  required
                  rows="1"
                  cols="40"
                  data-cy="booking-fees-additional-explanation"
                  onChange={(event) => setAdditionalExplanation(event.target.value)}
                />
              </ExplanationLabel>
            )}
          </ReasonSection>

          <CalculateRefund data-cy="booking-fees-calculate" disabled={!canCalculateRefund} loading={isRefunding} variant="primary">
            {formatMessage({ id: 'paymentsTab.calcRefund' })}
          </CalculateRefund>
        </BookingFeeFooter>
      )}

      <Modal
        title={formatMessage({ id: 'paymentsTab.modalTitle' })}
        isOpen={showModal}
        bottomRow={
          <>
            <ConfirmRefund
              data-cy="booking-fees-confirm"
              onClick={handleConfirm}
              disabled={loading}
              loading={isConfirming}
              variant="primary"
            >
              {formatMessage({ id: 'paymentsTab.confirmRefund' })}
            </ConfirmRefund>
            <CancelRefund data-cy="booking-fees-cancel" onClick={handleCancel} disabled={loading} loading={isCancelling}>
              {formatMessage({ id: 'paymentsTab.cancelRefund' })}
            </CancelRefund>
          </>
        }
      >
        <h3>
          {formatMessage({ id: 'paymentsTab.amountToBeRefunded' })}:
          <AmountToBeRefunded>
            <CurrencySymbol currencyCode={toBeRefunded.currency} />
            {Math.abs(toBeRefunded.amount)}
          </AmountToBeRefunded>
        </h3>
      </Modal>
    </form>
  );
};

BookingFees.propTypes = {
  toBeRefunded: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired
  }),
  bookingFees: productsShape.isRequired,
  hotelCancellationFees: PropTypes.arrayOf(Object),
  hotelMarginCancellationFees: PropTypes.arrayOf(Object),
  onRefund: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  isRefunding: PropTypes.bool,
  isCancelling: PropTypes.bool,
  isConfirming: PropTypes.bool,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  user: userShape,
  bookingStatus: PropTypes.string,
  disabled: PropTypes.bool,
  disabledTooltipMessageId: PropTypes.string
};

export default BookingFees;
